'use client';
import useFcmToken from '@/hooks/useFCMToken';
import { getMessaging, onMessage } from 'firebase/messaging';
import Link from 'next/link';
import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { Close } from './ui/Icons';
import useAppLocale from '@/hooks/useAppLocale';
import useLanguageChange from '@/hooks/useLanguageChange';
import Image from 'next/image';

export default function FcmTokenComp() {
  const { notificationPermissionStatus, firebaseApp, isSupported } =
    useFcmToken();
  const custLocale = useAppLocale();
  const resLocale = useLanguageChange();
  let appLocale: null | string = null;

  useEffect(() => {
    if (isSupported) {
      const isResUser = window.location.href.includes('/business');
      let notificationURL = '';

      if (isResUser) {
        appLocale = resLocale.localeCode;
        notificationURL = `${window.location.origin}/${appLocale}/business/dash/notifications`;
      } else {
        appLocale = custLocale.locale as string;
        notificationURL = `${window.location.origin}/${appLocale}/account?tab=notifications`;
      }
      if (notificationPermissionStatus === 'granted' && firebaseApp) {
        try {
          const messaging = getMessaging(firebaseApp);
          const unsubscribe = onMessage(messaging, (payload) => {
            toast.custom((t) => (
              <div
                className={`${
                  t.visible ? 'animate-enter' : 'animate-leave'
                } pointer-events-auto flex w-full max-w-md rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5`}
              >
                <Link href={notificationURL} className="w-full">
                  <div className="w-full flex-1 p-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 pt-0.5">
                        <Image
                          src={payload.notification?.icon ?? '/favicon.ico'}
                          alt="Notification icon"
                          width={40}
                          height={40}
                          className="rounded-full"
                        />
                      </div>
                      <div className="ml-3 flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {payload.notification?.title}
                        </p>
                        <p className="mt-1 text-sm text-gray-500">
                          {payload.notification?.body}
                        </p>
                      </div>
                    </div>
                  </div>
                </Link>
                <div className="flex border-l border-gray-200">
                  <button
                    onClick={() => toast.dismiss(t.id)}
                    className="flex w-full items-center justify-center rounded-none rounded-r-lg border border-transparent p-4 text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <Close />
                  </button>
                </div>
              </div>
            ));
          });
          return () => {
            unsubscribe(); // Unsubscribe from the onMessage event on cleanup
          };
        } catch (messagingError) {
          console.error(
            'Failed to initialize foreground messaging:',
            messagingError
          );
        }
      }
    }
  }, [notificationPermissionStatus, isSupported, firebaseApp]);

  return null; // This component is primarily for handling foreground notifications
}
